/* eslint-disable react/prop-types */
import Checkbox from '@material-ui/core/Checkbox'
import Drawer from '@material-ui/core/Drawer'
import FormControl from '@material-ui/core/FormControl'
import IconButton from '@material-ui/core/IconButton'
import InputLabel from '@material-ui/core/InputLabel'
import ListItemText from '@material-ui/core/ListItemText'
import MenuItem from '@material-ui/core/MenuItem'
import Select from '@material-ui/core/Select'
import { makeStyles } from '@material-ui/core/styles'
import CloseIcon from '@material-ui/icons/Close'
import FilterListIcon from '@material-ui/icons/FilterList'
import { isArray, range, isEmpty } from 'lodash'
import React from 'react'

import { fetchSubject } from 'api'
import { AutoComplete } from 'app/components/generic/FormFields'
import constants from 'app/constants'
import { eventEmitter } from 'app/helpers'
import { useIsMobile } from 'app/hooks/useIsMobile'
import { colors } from 'app/theme/colors'
import { space } from 'app/theme/tokens'

import { HeaderFields } from './config'
import * as Styles from './styles'

import {
  batchTypes,
  BookingTypes,
  FilterTypes,
  levelList,
  closedBatchFilterOptions
} from '../../constants'

const useStyles = makeStyles((theme) => ({
  formControl: {
    margin: theme.spacing(1),
    width: 300
  }
}))

const ages = range(3, 19).map((item) => ({ uuid: item.toString(), name: item }))

const Header = ({
  selectedBatchType,
  selectedGrades,
  selectedCategories,
  selectedLevel,
  selectedClosedBatch,
  onChangeFilter,
  isActiveCareOrPreschool,
  selectedCentres
}) => {
  const classes = useStyles()
  const isMobile = useIsMobile()
  const [mobileFiltersOpen, setMobileFiltersOpen] = React.useState(false)

  /**
   * FIXME: this listener is only to force refresh new schedules data
   * after successful booking, remove this later if any better way available to do this
   */
  React.useEffect(() => {
    const handler = () => {
      onChangeFilter(FilterTypes.CATEGORY, [...selectedCategories])
    }

    eventEmitter.addListener(constants.INVENTORY_BOOKING_SUCCESS, handler)

    return () => {
      eventEmitter.removeListener(constants.INVENTORY_BOOKING_SUCCESS, handler)
    }
  }, [selectedCategories])

  const handleOnChange = (event) => {
    const { name, value } = event.target
    onChangeFilter(name, value)

    if (name === FilterTypes.BATCH_TYPE) {
      // store result in local storage
      localStorage.setItem('SELECTED_BATCH_TYPE', JSON.stringify(value))
    }
  }

  const handleAutoCompleteOnChange = async (name, values) => {
    onChangeFilter(name, values)

    if (name === FilterTypes.CENTRE) {
      // store result in local storage
      localStorage.setItem('SELECTED_CENTRE', JSON.stringify(values))
      const options = await fetchSubjectValues()
    }
  }

  const getValue = (name) => {
    switch (name) {
      case FilterTypes.BATCH_TYPE:
        return selectedBatchType
      case FilterTypes.LEVEL:
        return selectedLevel
      case FilterTypes.CLOSED_BATCH:
        return selectedClosedBatch

      default:
        return []
    }
  }

  const renderValues = (name) => {
    switch (name) {
      case FilterTypes.BATCH_TYPE:
        return batchTypes
      case FilterTypes.LEVEL:
        return levelList
      case FilterTypes.CATEGORY:
        return fetchSubjectValues
      case FilterTypes.CLOSED_BATCH:
        return closedBatchFilterOptions

      default:
        return []
    }
  }

  const fetchSubjectValues = async (searchedText) => {
    try {
      const localStorageCentre = JSON.parse(
        localStorage.getItem('SELECTED_CENTRE')
      )

      const queries = {
        limit: 600,
        centre_uuid: localStorageCentre.value
      }

      if (!isEmpty(searchedText)) {
        // Backend SubjectFilter exposes the name search under `subject`
        // (icontains on Subject.name); there is no `name` filter, so sending
        // `name` is silently ignored and every subject comes back unfiltered.
        queries.subject = searchedText
      }

      const response = await fetchSubject({ queries })

      let values = response.results.map((item) => ({
        value: item.uuid,
        name: item.name
      }))

      values.unshift({ value: null, name: 'ALL SUBJECTS' })

      return values
    } catch (error) {
      console.log('Error in fetching subjects :- ', error)
      throw error
    }
  }

  const centreDefaultValue = localStorage.getItem('SELECTED_CENTRE')
    ? JSON.parse(localStorage.getItem('SELECTED_CENTRE'))
    : null

  const modifiedHeaderField =
    selectedBatchType === BookingTypes.EXCEL
      ? [
          ...HeaderFields,
          {
            name: FilterTypes.LEVEL,
            label: 'Batch Level',
            inputType: 'select',
            disabledInPreschoolAC: true
          }
        ]
      : HeaderFields

  // Filter out closed batch filter for school and care batch types
  const filteredHeaderFields = modifiedHeaderField.filter((field) => {
    if (field.name === FilterTypes.CLOSED_BATCH) {
      return !['school', 'care'].includes(selectedBatchType)
    }
    return true
  })

  const renderFields = () => {
    return (
      <>
        {filteredHeaderFields.map((field) => {
          if (field.inputType === 'select') {
            return (
              <FormControl
                key={field.name}
                variant="outlined"
                className={classes.formControl}
              >
                <InputLabel>{field.label}</InputLabel>
                <Select
                  value={getValue(field.name)}
                  name={field.name}
                  label={field.label}
                  disabled={
                    isActiveCareOrPreschool &&
                    field.disabledInPreschoolAC === true
                  }
                  onChange={handleOnChange}
                  renderValue={(selectedItems) => {
                    return isArray(selectedItems)
                      ? selectedItems
                          .map((selectedItem) => {
                            return renderValues(field.name).find(
                              (item) => item.uuid === selectedItem
                            ).name
                          })
                          .join(', ')
                      : renderValues(field.name).find(
                          (item) => item.uuid === selectedItems
                        ).name
                  }}
                  {...(field.multiple ? { multiple: true } : null)}
                >
                  {renderValues(field.name).map((value, index) => (
                    <MenuItem key={index} value={value.uuid}>
                      {field.multiple && (
                        <Checkbox
                          color="primary"
                          checked={
                            getValue(field.name).indexOf(value.uuid) > -1
                          }
                        />
                      )}
                      <ListItemText primary={value.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )
          } else if (field.inputType === 'autocomplete') {
            return (
              <FormControl
                key={field.name}
                variant="outlined"
                className={classes.formControl}
              >
                <AutoComplete
                  async={field.async}
                  name={field.name}
                  label={field.label}
                  disabled={
                    isActiveCareOrPreschool &&
                    field.disabledInPreschoolAC === true
                  }
                  multiple={field.multiple}
                  fetchValues={field.fetchValues || renderValues(field.name)}
                  onChange={handleAutoCompleteOnChange}
                  initialSelectIndex={field.initialSelectIndex}
                  minCharactersToSearch={field.minCharactersToSearch}
                  searchWithEmptyValue={true}
                  {...(field.name === FilterTypes.CENTRE && centreDefaultValue
                    ? { defaultValue: centreDefaultValue }
                    : null)}
                />
              </FormControl>
            )
          }

          return null
        })}
      </>
    )
  }

  // On mobile: collapse the six filter fields (centre / batch type / grade /
  // category / level / closed-batch) into a single Filters button + bottom
  // drawer. Same fields, same handlers — just hidden until the user wants
  // them. Active-filter count goes on the badge so the user can see at a
  // glance that something is narrowing the result list.
  const activeFilterCount =
    (selectedCentres && selectedCentres.value ? 1 : 0) +
    ((selectedGrades || []).length > 0 ? 1 : 0) +
    ((selectedCategories || []).length > 0 ? 1 : 0) +
    (selectedLevel ? 1 : 0) +
    (selectedClosedBatch && selectedClosedBatch !== 'hide_closed' ? 1 : 0)

  return (
    <Styles.Container>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap'
        }}
      >
        <Styles.Heading>Batches</Styles.Heading>
        {isMobile && (
          <button
            type="button"
            onClick={() => setMobileFiltersOpen(true)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              background: colors.white,
              border: `1px solid ${colors.borderDefault}`,
              borderRadius: 8,
              padding: '10px 14px',
              fontSize: 14,
              fontWeight: 600,
              color: colors.textPrimary,
              cursor: 'pointer'
            }}
            aria-label="open filters"
          >
            <FilterListIcon fontSize="small" />
            Filters
            {activeFilterCount > 0 && (
              <span
                style={{
                  background: colors.ohOrange,
                  color: colors.white,
                  borderRadius: 999,
                  minWidth: 20,
                  height: 20,
                  padding: '0 6px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 12
                }}
              >
                {activeFilterCount}
              </span>
            )}
          </button>
        )}
      </div>
      {!isMobile && <Styles.FormFields>{renderFields()}</Styles.FormFields>}
      <Drawer
        anchor="bottom"
        open={isMobile && mobileFiltersOpen}
        onClose={() => setMobileFiltersOpen(false)}
        PaperProps={{
          style: {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            padding: space.md,
            maxHeight: '85vh'
          }
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: space.md
          }}
        >
          <span style={{ fontSize: 18, fontWeight: 600 }}>Filters</span>
          <IconButton
            aria-label="close filters"
            size="small"
            onClick={() => setMobileFiltersOpen(false)}
          >
            <CloseIcon />
          </IconButton>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: space.sm }}>
          {renderFields()}
        </div>
      </Drawer>
    </Styles.Container>
  )
}

export default Header
